import { useEffect, useState } from "react";
import {
  Building2,
  ExternalLink,
  Loader2,
  Mail,
  Phone,
  Ticket,
  UserRound,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

function Value({ children }) {
  return children || <span className="text-muted-foreground">—</span>;
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-background/40 p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold">{value ?? 0}</div>
    </div>
  );
}

export default function ClientDetailsDialog({
  client,
  open,
  onOpenChange,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !client?.id) return;

    setLoading(true);
    setData(null);

    api.get(`/organizations/${client.id}/summary`)
      .then((response) => setData(response.data))
      .catch((error) => toast.error(formatApiError(error)))
      .finally(() => setLoading(false));
  }, [open, client?.id]);

  const details = data?.client || client || {};
  const statistics = data?.statistics || {};
  const primaryContact =
    details.primary_contact ||
    {
      name: details.contact_person,
      designation: details.contact_designation,
      email: details.contact_email,
      phone: details.contact_phone,
    };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="left-0 top-0 h-screen w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-none border-0 p-0 sm:rounded-none"
        data-testid="client-details-dialog"
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 border-b border-border bg-card/95 px-6 py-5 pr-14">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-2xl font-extrabold">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/10">
                  <Building2 className="h-5 w-5 text-[#e5c158]" />
                </span>
                {details.name || "Client details"}
              </DialogTitle>
              <DialogDescription>
                {details.code || "—"} · Complete client information, users and tickets
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-7xl px-6 py-7">
              {loading ? (
                <div className="py-24 text-center text-muted-foreground">
                  <Loader2 className="mx-auto mb-3 h-7 w-7 animate-spin" />
                  Loading client information…
                </div>
              ) : (
                <Tabs defaultValue="overview" className="space-y-5">
                  <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="users">
                      Users ({statistics.total_users || 0})
                    </TabsTrigger>
                    <TabsTrigger value="tickets">
                      Tickets ({statistics.total_tickets || 0})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-5">
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
                      <Metric label="Users" value={statistics.total_users} />
                      <Metric label="Client Admins" value={statistics.client_admins} />
                      <Metric label="Client Users" value={statistics.client_users} />
                      <Metric label="Total Tickets" value={statistics.total_tickets} />
                      <Metric label="Open" value={statistics.open_tickets} />
                      <Metric label="Resolved" value={statistics.resolved_tickets} />
                      <Metric label="Closed" value={statistics.closed_tickets} />
                      <Metric label="Escalated" value={statistics.escalated_tickets} />
                    </div>

                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                      <section className="rounded-xl border border-border bg-card/60 p-5">
                        <h3 className="mb-4 font-display text-lg font-bold">
                          Client information
                        </h3>

                        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                          <div>
                            <div className="text-xs text-muted-foreground">Client name</div>
                            <div className="mt-1 font-semibold"><Value>{details.name}</Value></div>
                          </div>

                          <div>
                            <div className="text-xs text-muted-foreground">Client code</div>
                            <div className="mt-1 font-semibold"><Value>{details.code}</Value></div>
                          </div>

                          <div>
                            <div className="text-xs text-muted-foreground">Legal name</div>
                            <div className="mt-1"><Value>{details.legal_name}</Value></div>
                          </div>

                          <div>
                            <div className="text-xs text-muted-foreground">Industry</div>
                            <div className="mt-1"><Value>{details.industry}</Value></div>
                          </div>

                          <div>
                            <div className="text-xs text-muted-foreground">Status</div>
                            <div className="mt-1 capitalize"><Value>{details.status}</Value></div>
                          </div>

                          <div>
                            <div className="text-xs text-muted-foreground">GST number</div>
                            <div className="mt-1"><Value>{details.gst_number}</Value></div>
                          </div>

                          <div className="sm:col-span-2">
                            <div className="text-xs text-muted-foreground">Website</div>
                            <div className="mt-1">
                              {details.website ? (
                                <a
                                  href={details.website}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[#e5c158] hover:underline"
                                >
                                  {details.website}
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              ) : (
                                <Value />
                              )}
                            </div>
                          </div>
                        </div>
                      </section>

                      <section className="rounded-xl border border-border bg-card/60 p-5">
                        <h3 className="mb-4 font-display text-lg font-bold">
                          Primary point of contact
                        </h3>

                        <div className="space-y-4 text-sm">
                          <div className="flex items-start gap-3">
                            <UserRound className="mt-0.5 h-4 w-4 text-[#e5c158]" />
                            <div>
                              <div className="font-semibold">
                                <Value>{primaryContact?.name}</Value>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                <Value>{primaryContact?.designation}</Value>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <Mail className="h-4 w-4 text-[#e5c158]" />
                            <Value>{primaryContact?.email}</Value>
                          </div>

                          <div className="flex items-center gap-3">
                            <Phone className="h-4 w-4 text-[#e5c158]" />
                            <Value>{primaryContact?.phone}</Value>
                          </div>
                        </div>
                      </section>

                      <section className="rounded-xl border border-border bg-card/60 p-5 lg:col-span-2">
                        <h3 className="mb-3 font-display text-lg font-bold">
                          Address
                        </h3>
                        <div className="text-sm leading-6">
                          <Value>
                            {[
                              details.address,
                              details.city,
                              details.state,
                              details.country,
                              details.postal_code,
                            ].filter(Boolean).join(", ")}
                          </Value>
                        </div>
                      </section>
                    </div>
                  </TabsContent>

                  <TabsContent value="users">
                    <div className="overflow-hidden rounded-xl border border-border bg-card/60">
                      <table className="w-full text-sm">
                        <thead className="border-b border-border text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                          <tr>
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Role</th>
                            <th className="px-4 py-3">Phone</th>
                            <th className="px-4 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data?.users || []).map((user) => (
                            <tr key={user.id} className="border-b border-border/60">
                              <td className="px-4 py-3 font-semibold">{user.name}</td>
                              <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                              <td className="px-4 py-3">{ROLE_LABEL[user.role] || user.role}</td>
                              <td className="px-4 py-3">{user.phone || "—"}</td>
                              <td className="px-4 py-3 capitalize">{user.status}</td>
                            </tr>
                          ))}

                          {!data?.users?.length && (
                            <tr>
                              <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                                No users found for this client.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>

                  <TabsContent value="tickets">
                    <div className="overflow-hidden rounded-xl border border-border bg-card/60">
                      <table className="w-full text-sm">
                        <thead className="border-b border-border text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                          <tr>
                            <th className="px-4 py-3">Ticket</th>
                            <th className="px-4 py-3">Title</th>
                            <th className="px-4 py-3">Raised by</th>
                            <th className="px-4 py-3">Priority</th>
                            <th className="px-4 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data?.recent_tickets || []).map((ticket) => (
                            <tr key={ticket.id} className="border-b border-border/60">
                              <td className="px-4 py-3 font-mono text-xs">
                                {ticket.ticket_number}
                              </td>
                              <td className="max-w-md truncate px-4 py-3 font-semibold">
                                {ticket.title}
                              </td>
                              <td className="px-4 py-3">
                                {ticket.raised_by_name || "—"}
                              </td>
                              <td className="px-4 py-3">{ticket.priority}</td>
                              <td className="px-4 py-3 capitalize">
                                {String(ticket.status || "").replaceAll("_", " ")}
                              </td>
                            </tr>
                          ))}

                          {!data?.recent_tickets?.length && (
                            <tr>
                              <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                                No tickets found for this client.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
